import {
  addDays,
  formatISO,
  parseISO,
  startOfDay,
  isBefore,
  isSameDay,
  format,
} from "date-fns";
import { es } from "date-fns/locale";

/**
 * getToday
 * 📥 Input: none
 * 📤 Output: Fecha actual a las 00:00 (start of day)
 * Ejemplo: new Date("2025-08-04T18:00:00Z") → "2025-08-04T00:00:00"
 */
export const getToday = () => startOfDay(new Date());

/**
 * parseDueDate
 * 📥 Input: "2025-08-04T14:35:00Z"
 * 📤 Output: Date → "2025-08-04T00:00:00"
 */
export const parseDueDate = (dueDate: string) => startOfDay(parseISO(dueDate));

/**
 * toISODate
 * 📥 Input: new Date("2025-08-04T18:00:00Z")
 * 📤 Output: "2025-08-04"
 */
export const toISODate = (date: Date) =>
  formatISO(startOfDay(date), { representation: "date" });

/**
 * addVisibleDay
 * 📥 Input: new Date("2025-08-04"), offset: 2
 * 📤 Output: "2025-08-06T00:00:00"
 */
export const addVisibleDay = (date: Date, offset: number) =>
  startOfDay(addDays(date, offset));

/**
 * getNextDay
 * 📥 Input: new Date("2025-08-04")
 * 📤 Output: "2025-08-05T00:00:00"
 */
export const getNextDay = (date: Date) => addVisibleDay(date, 1);

/**
 * getPreviousDay
 * 📥 Input: new Date("2025-08-04")
 * 📤 Output: "2025-08-03T00:00:00"
 */
export const getPreviousDay = (date: Date) => addVisibleDay(date, -1);

/**
 * formatDayTitle
 * 📥 Input: new Date("2025-08-04")
 * 📤 Output: "lunes"
 */
export const formatDayTitle = (date: Date) =>
  format(date, "EEEE", { locale: es });

/**
 * formatDaySubtitle
 * 📥 Input: new Date("2025-08-04")
 * 📤 Output: "4 de agosto"
 */
export const formatDaySubtitle = (date: Date) =>
  format(date, "d 'de' MMMM yyyy", { locale: es });

/**
 * getVisibleDays
 * 📥 Input: startDate = "2025-08-01", numberOfDays = 3
 * 📤 Output: ["2025-08-01", "2025-08-02", "2025-08-03"]
 */
export const getVisibleDays = (startDate: Date, numberOfDays: number) =>
  Array.from({ length: numberOfDays }, (_, i) => addVisibleDay(startDate, i));

/**
 * isSameDayISO
 * 📥 Input: "2025-08-04", "2025-08-04T23:59:59"
 * 📤 Output: true
 */
export const isSameDayISO = (a: Date, b: Date) =>
  isSameDay(startOfDay(a), startOfDay(b));

/**
 * isOverdue
 * 📥 Input: dueDate = "2025-08-01", isCompleted = false
 * 📤 Output: true (si la fecha ya pasó y no se completó)
 */
export const isOverdue = (dueDate: Date, isCompleted: boolean) =>
  !isCompleted && isBefore(dueDate, getToday());

/**
 * formatFullNumericDate
 * 📥 Input: "2025-08-04T14:35:00Z"
 * 📤 Output: "04/08/2025"
 */
export const formatFullNumericDate = (date: string | Date) => {
  const parsedDate = typeof date === "string" ? parseISO(date) : date;
  return format(parsedDate, "dd/MM/yy", { locale: es });
};

/**
 * Formatea una fecha ISO como "dd/MM/yyyy" ignorando la hora (UTC seguro).
 * Ideal para campos como `created_at` donde no te interesa la hora.
 */
export const formatFullNumericDateUTC = (date: string | Date) => {
  const dateOnlyISO =
    typeof date === "string"
      ? date.slice(0, 10) // "2025-08-03T03:51:58..." => "2025-08-03"
      : date.toISOString().slice(0, 10); // Date => "2025-08-03"

  return format(parseISO(dateOnlyISO), "dd/MM/yy");
};

/**
 * Flexible date search matcher
 * Supports multiple formats:
 * - DD/MM/YYYY (17/10/2025)
 * - YYYY-MM-DD (2025-10-17)
 * - MM/YYYY (10/2025) - matches all dates in that month
 * - YYYY (2025) - matches all dates in that year
 * - Partial matches
 * 
 * 📥 Input: dateString = "2025-10-17", searchTerm = "17/10/2025" or "10/2025" or "2025"
 * 📤 Output: true if matches
 */
export const matchesDateSearch = (dateString: string, searchTerm: string): boolean => {
  if (!dateString || !searchTerm) return false;
  
  // Parse the date from database (YYYY-MM-DD format)
  const [year, month, day] = dateString.split('-');
  if (!year || !month || !day) return false;
  
  // Format in different representations for comparison
  const ddmmyyyy = `${day}/${month}/${year}`; // 17/10/2025
  const mmyyyy = `${month}/${year}`; // 10/2025
  const yyyymmdd = dateString; // 2025-10-17
  
  // Normalize search term (remove extra spaces)
  const normalizedSearch = searchTerm.trim();
  
  // Try exact matches first
  if (ddmmyyyy === normalizedSearch) return true;
  if (yyyymmdd === normalizedSearch) return true;
  if (mmyyyy === normalizedSearch) return true;
  if (year === normalizedSearch) return true;
  
  // Try partial matches (for flexible search)
  if (ddmmyyyy.includes(normalizedSearch)) return true;
  if (yyyymmdd.includes(normalizedSearch)) return true;
  if (mmyyyy.includes(normalizedSearch)) return true;
  
  return false;
};

/**
 * formatMonthYearText
 * 📥 Input: "2025-08-04"
 * 📤 Output: "agosto 2025"
 */
export const formatMonthYearText = (date: string | Date) => {
  const parsedDate = typeof date === "string" ? parseISO(date) : date;
  return format(parsedDate, "MMMM yyyy", { locale: es });
};

/**
 * formatLongDateWithWeekday
 * 📥 Input: "2025-08-04"
 * 📤 Output: "lunes 4 de agosto de 2025"
 */
export const formatLongDateWithWeekday = (date: string | Date) => {
  const parsedDate = typeof date === "string" ? parseISO(date) : date;
  return format(parsedDate, "EEEE d 'de' MMMM 'de' yyyy", { locale: es });
};

/**
 * formatDateRange
 * 📥 Input: "2025-11-28", "2025-12-04"
 * 📤 Output: "Del 28 de noviembre al 04 de diciembre"
 */
export const formatDateRange = (startDate: string, endDate: string): string => {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  
  return `Del ${format(start, "d 'de' MMMM", { locale: es })} al ${format(end, "dd 'de' MMMM", { locale: es })}`;
};
